import { produce } from 'immer';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { type SubmissionDetail } from '@ifa/types';

export type AppStatus = 'idle' | 'fetching' | 'error';
export type NewStatus = { status: 'error'; error: unknown } | { status: Omit<AppStatus, 'error'>; error?: never };

type IfaStoreState = {
    app: {
        error: unknown;
        status: AppStatus;
    };
    drawer: {
        opened: boolean;
        currentItem: SubmissionDetail | 'Item not found' | undefined;
    };
    table: {
        isEmpty: boolean;
        firstLoad: boolean;
        data: SubmissionDetail[];
        shouldRefetch: boolean;
        shouldShortenPollTimeFromRequest: boolean;
    };
    user: {
        tcIds: string[];
    };
};

type IfaStoreActions = {
    app: {
        setStatus: (newStatus: NewStatus) => void;
    };
    drawer: {
        openDrawer: (id: number) => void;
        closeDrawer: () => void;
        delayedClearCurrentItem: () => void;
        setCurrentItem: (data: SubmissionDetail) => void;
    };
    table: {
        setData: (data: SubmissionDetail[]) => void;
        setShouldRefetch: (flag: boolean) => void;
        setShouldShortenPollTimeFromRequest: (flag: boolean) => void;
        getById: (id: number) => SubmissionDetail | undefined;
    };
    user: {
        setTcIds: (tcIds: string[]) => void;
    };
    reset: () => void;
};

export type IfaStore = IfaStoreState & IfaStoreActions;

export const defaultIfaStoreState: IfaStoreState = {
    app: {
        error: null,
        status: 'idle',
    },
    drawer: {
        opened: false,
        currentItem: undefined,
    },
    table: {
        isEmpty: true,
        firstLoad: true,
        data: [],
        shouldRefetch: false,
        shouldShortenPollTimeFromRequest: false,
    },
    user: {
        tcIds: [],
    },
};

export const useIfaStore = create<IfaStore>()(
    devtools(
        (set, get) => ({
            app: {
                ...defaultIfaStoreState.app,
                setStatus: (newStatus: NewStatus) =>
                    set(
                        produce(state => {
                            state.app.status = newStatus.status;
                            if (newStatus.error) {
                                state.app.error = newStatus.error;
                            }
                        })
                    ),
            },
            drawer: {
                ...defaultIfaStoreState.drawer,
                openDrawer: id =>
                    set(
                        produce(state => {
                            const tableData = get().table.data;
                            if (!tableData) {
                                return;
                            }

                            state.drawer.currentRid = id;
                            state.drawer.opened = true;

                            const item = tableData.find(item => item.id === id);

                            if (!item) {
                                state.drawer.currentItem = 'Item not found';
                            } else {
                                state.drawer.currentItem = item;
                            }
                        })
                    ),
                closeDrawer: () =>
                    set(
                        produce(state => {
                            state.drawer.opened = false;
                            state.drawer.delayedClearCurrentItem();
                        })
                    ),
                delayedClearCurrentItem: () => {
                    // Clearing the current item needs to be delayed to prevent the drawer from clearing before the animation is finished
                    setTimeout(() => {
                        set(
                            produce(state => {
                                state.drawer.currentItem = undefined;
                            })
                        );
                    }, 250);
                },
                setCurrentItem: currentItem => {
                    set(
                        produce(state => {
                            state.drawer.currentItem = currentItem;
                        })
                    );
                },
            },
            table: {
                ...defaultIfaStoreState.table,
                setData: data => {
                    if (data) {
                        set(
                            produce(state => {
                                state.table.data = data;
                                state.table.isEmpty = data.length === 0;

                                if (state.table.firstLoad) {
                                    state.table.firstLoad = false;
                                }
                            })
                        );
                    }
                },
                setShouldRefetch: flag => {
                    set(
                        produce(state => {
                            state.table.shouldRefetch = flag;
                        })
                    );
                },
                setShouldShortenPollTimeFromRequest: flag =>
                    set(
                        produce(state => {
                            state.table.shouldShortenPollTimeFromRequest = flag;
                        })
                    ),
                getById: id => get().table.data.find(item => item.id === id),
            },
            user: {
                ...defaultIfaStoreState.user,
                setTcIds: tcIds =>
                    set(
                        produce(state => {
                            state.user.tcIds = tcIds;
                        })
                    ),
            },
            reset: () =>
                set(
                    produce(state => {
                        state.app = { ...defaultIfaStoreState.app, ...state.app };
                        state.drawer = { ...defaultIfaStoreState.drawer, ...state.drawer };
                        state.table = { ...defaultIfaStoreState.table, ...state.table };
                    })
                ),
        }),
        {
            enabled: process.env.NODE_ENV === 'development',
        }
    )
);

export const getTransplantCenterIds = (state: IfaStore): number[] => {
    return [...new Set(state.table.data.map(item => item.tcId))].sort((a, b) => a - b);
};
